import { useState, useRef, useContext, useEffect } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box, Stack } from "ui";
import { Popover } from "ui/popover";
import { AnnotationCommentDisplay } from "containers/admin/clients/touchpoint/components/comments/annotation-comment-display/annotation-comment-display";
import { AnnotationCommentInput } from "containers/admin/clients/touchpoint/components/comments/annotation-comment-input/annotation-comment-input";
import {
  CommentPopoverButton,
  CommentPopoverButtonProps,
} from "containers/admin/clients/touchpoint/components/comments/comment-popover-button/comment-popover-button";

import CommentContext from "contexts/comment-context";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useAppendCommentReply, useCommentReplies } from "hooks/api/use-touchpoint-comments";

import { hasPermission } from "utilities/user";
import useOnScreen from "hooks/use-on-screen";

import { Comment, NewComment } from "types/comments";
import { Permission } from "types/auth";

/**
 * The thread is the one part that gives way under the popover's 420px cap: `minHeight: 0` lets it
 * shrink inside the body's flex column and scroll on its own, so the reply input stays in view below it
 * and `scrollRef` still scrolls the thread rather than the whole panel.
 */
const PANEL_CONTENT_SX: SxProps<Theme> = { width: "100%", minHeight: 0 };

const THREAD_SX: SxProps<Theme> = (theme) => ({
  textAlign: "left",
  minHeight: 0,
  overflowY: "auto",
  pb: 0.5,
  border: `1px solid ${theme.palette.divider}`,
});

interface CommentPopoverProps {
  moduleId: string; // Move this to the input
  comment?: Comment;
  commentTriggerProps?: CommentPopoverButtonProps;
  isCommentPopoverOpen: boolean;
  isModuleHovered: boolean;
  closeCommentPopover(): void;
  toggleCommentPopover(): void;
}
export const CommentPopover = ({
  moduleId,
  comment,
  commentTriggerProps,
  isCommentPopoverOpen,
  isModuleHovered,
  closeCommentPopover,
  toggleCommentPopover,
  ...rest
}: CommentPopoverProps) => {
  const currentUser = useCurrentUser();
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();

  const isClientUser = hasPermission(currentUser, Permission.PERM_CLIENT_USER);

  const [isCommentInputExpanded, setIsCommentInputExpanded] = useState(true);

  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { submitNewComment, submitNewCommentReply, commentingDisabled } = useContext(CommentContext);

  const scope = {
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  };

  /**
   * One `CommentPopover` is mounted per commentable block, so an ungated fetch costs a request per
   * comment on the page every time it runs. The thread is only rendered while the popover is open, and
   * `AnnotationCommentDisplay` hides it entirely at `replyCount === 0` — neither case needs the call.
   */
  const { data: replyPage } = useCommentReplies(scope, comment?.id, {
    enabled: isCommentPopoverOpen && (comment?.replyCount ?? 0) > 0,
  });
  const appendCommentReply = useAppendCommentReply(scope, comment?.id);

  function handleSubmit(submission: NewComment) {
    if (!comment) {
      const newComment: NewComment = { ...submission, ...{ annotationId: moduleId } };
      return submitNewComment(newComment);
    }
    const newReply: NewComment = { ...submission, ...{ parentId: comment.id } };
    return submitNewCommentReply(newReply)
      .then((reply) => {
        if (reply) {
          appendCommentReply(reply);
          scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      })
      .catch((err) => console.error("error submitting comment reply", err));
  }

  const triggerRef = useRef<HTMLButtonElement>(null);
  const isTriggerWithinViewport = useOnScreen(triggerRef);
  const isTriggerVisible = !!comment || isModuleHovered || isCommentPopoverOpen;

  useEffect(() => {
    /**
     * Hide popover when it's scrolled out of viewport  */
    if (isCommentPopoverOpen && !isTriggerWithinViewport) {
      closeCommentPopover();
    }
  }, [closeCommentPopover, isCommentPopoverOpen, isTriggerWithinViewport]);

  return (
    <Popover
      open={isCommentPopoverOpen}
      onOpen={toggleCommentPopover}
      onClose={closeCommentPopover}
      placement="bottom"
      arrow
      keepPageInteractive
      triggerElement={
        <CommentPopoverButton
          ref={triggerRef}
          comment={comment}
          isVisible={isTriggerVisible}
          {...commentTriggerProps}
        />
      }
      {...rest}>
      <Stack spacing={1} sx={PANEL_CONTENT_SX}>
        {!!comment && (
          <Box ref={scrollRef} sx={THREAD_SX}>
            <AnnotationCommentDisplay
              comment={comment}
              isClientUser={isClientUser}
              replies={replyPage?.items ?? []}
            />
          </Box>
        )}
        <AnnotationCommentInput
          isDisabled={comment ? !comment.open : commentingDisabled}
          parentId={comment ? comment.id : undefined}
          commentType={comment ? "reply" : "main"}
          isClientUser={isClientUser}
          submitNewComment={handleSubmit}
          onCancel={closeCommentPopover}
          commentInputRef={commentInputRef}
          isExpanded={isCommentInputExpanded}
          setIsExpanded={setIsCommentInputExpanded}
        />
      </Stack>
    </Popover>
  );
};
